import { isValidObjectId } from "@/lib/utils/input-validator";
import { useEffect, useRef, useState } from "react";
import { FormInput } from "../../Inputs/FormInput";

type TableCellContentObjectIdProps = {
  value: string;
  border?: string;
  isFilter?: boolean;
  isEditing: boolean;
  onFocusAction?: () => void;
  onBlurAction?: () => void; // Optional blur handler
  onChangeValue: (value: string) => void;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
};

export const TableCellContentObjectId: React.FC<
  TableCellContentObjectIdProps
> = ({
  value,
  border,
  isFilter,
  isEditing,
  onFocusAction,
  onBlurAction,
  onChangeValue,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  viewOnly,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // The input below is reachable ONLY in filter mode (the edit branch renders
  // nothing), so the state must hold whatever is being typed. Gating it on
  // isValidObjectId made the box inert: a 24-hex id is invalid at every
  // keystroke but the last, so the character never landed and the field could
  // never be filled by hand — only by pasting a complete id in one event.
  // The read branch keeps the validity check so a junk stored value isn't
  // rendered as text.
  const [objectId, setObjectIdState] = useState(value ?? "");

  // Resync when the cell is reused for a different value (sibling TCC* cells
  // all do this; without it a view/filter cell can show a stale id).
  useEffect(() => {
    setObjectIdState(value ?? "");
  }, [value]);

  function setObjectId(objectId: string) {
    setObjectIdState(objectId);
  }
  const handleChange = () => {
    onChangeValue(objectId);
    if (onBlurAction) onBlurAction();
  };

  return (
    <>
      {/* isFilter is checked BEFORE isEditing. The only host that reaches this
          component with isFilter is FilterInput/InputValue (popup filter
          builder and website-builder privacy rows) — and it
          passes isEditMode alongside isFilter, so the old order sent it to the
          empty edit branch and the OBJECT_ID value cell rendered NOTHING. The
          inline table filter row never gets here: useFilterCellHandling's
          isFilterable excludes OBJECT_ID outright. */}
      {isFilter ? (
        <FormInput
          ref={inputRef}
          disabled={viewOnly}
          id={`edit-${value}`}
          border={border}
          rounded={rounded}
          onBlurAction={handleChange}
          onFocusAction={onFocusAction}
          onEnterAction={() => inputRef?.current?.blur()}
          margin=""
          height={height}
          fontSize={fontSize}
          surface={surface}
          paddingInput={padding}
          value={objectId}
          onChangeAction={setObjectId}
          width="flex-1"
        />
      ) : isEditing ? (
        <></>
      ) : (
        <>{isValidObjectId(objectId) ? objectId : ""} </>
      )}
    </>
  );
};
